import React, {useCallback, useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import UndoIcon from "@mui/icons-material/Undo";
import {
  SortableContext,
  SortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {GridColDef} from "@mui/x-data-grid";
import {toast} from "sonner";
import {Box, Chip, Colors, Container, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, ListPageHeader, RowActions, SearchInput} from "../../ui/components";
import DeleteSegmentDialog from "./DeleteSegmentDialog";
import {useGetSegments} from "../../apis/requests/segments/getSegments";
import {useSaveSegmentsBatch} from "../../apis/requests/segments/saveSegmentsBatch";
import {ReachSegment, ReachSegmentCreatePayload, SegmentPageKind} from "../../apis/requests/segments/types";
import SegmentDialog from "./SegmentDialog";
import {
  CATEGORY_FALLBACK_CONDITIONS_LABEL,
  SEGMENT_CATEGORIES,
  SEGMENT_CATEGORY_INFO,
  SEGMENT_TYPE_INFO,
} from "./segment-form/constants";
import {segmentPageKindOf} from "./segment-form/helpers";
import {DndEnabledContext, DragHandleCell} from "../../components/sortable";
import {CATEGORY_HEADER_HEIGHT, CategoryHeaderInfo} from "./CategoryHeader";
import {
  CategoryDragContext,
  CategoryHeaderContext,
  IDLE_CATEGORY_DRAG,
  SegmentTypeSeparatorContext,
} from "./SegmentDndContext";
import {SEGMENT_TYPE_SEPARATOR_HEIGHT} from "./SegmentTypeSeparator";
import {SortableSegmentRow} from "./SortableSegmentRow";
import {StagedSegment, StagedStatus, useStagedSegments} from "./useStagedSegments";
import {
  CATEGORY_BLOCK_SPACING,
  blockDisplacement,
  blockKeyOf,
  categoryBlockPositions,
  moveBlock,
  rankByBlock,
} from "./segmentCategoryBlocks";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const FIRST_GENERAL_ROW_CLASS_NAME = "first-general-segment-row";
const DELETED_ROW_CLASS_NAME = "deleted-segment-row";
const CATEGORY_ROW_CLASS_NAME = "segment-category-row";
const CATEGORY_FIRST_ROW_CLASS_NAME = "segment-category-first-row";
const CATEGORY_ACCENT_WIDTH = 3;

const STAGED_STATUS_CHIP: Record<StagedStatus, {label: string; variant: "success" | "warning" | "error"}> = {
  new: {label: "New", variant: "success"},
  edited: {label: "Edited", variant: "warning"},
  deleted: {label: "Deleted", variant: "error"},
};

const isExclusive = (segment: ReachSegment | undefined) => segment?.type === "EXCLUSIVE";

const PAGE_TITLE_BY_PAGE_KIND: Record<SegmentPageKind, string> = {
  marketing: "Marketing segments",
  validation: "Validation segments",
};

type Props = {
  pageKind: SegmentPageKind;
};

const SegmentsPage = ({pageKind}: Props) => {
  const {data, isLoading, error} = useGetSegments();
  const {mutate: saveSegmentsBatch, isPending: isSaving} = useSaveSegmentsBatch();
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: DEFAULT_PAGE_SIZE});

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const serverSorted = useMemo(
    () => (data ?? []).filter((s) => segmentPageKindOf(s) === pageKind).sort((a, b) => a.priority - b.priority),
    [data, pageKind]
  );
  const {
    segments: orderedSegments,
    changeCount,
    stageCreate,
    stageUpdate,
    stageDelete,
    stageOrder,
    undo,
    discardAll,
    buildBatchPayload,
  } = useStagedSegments(serverSorted);
  const hasChanges = changeCount > 0;
  const segmentById = useMemo(() => new Map(orderedSegments.map((s) => [s.id, s])), [orderedSegments]);

  const filteredSegments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orderedSegments;
    return orderedSegments.filter(
      (s) => s.name.toLowerCase().includes(q) || (s.description ?? "").toLowerCase().includes(q)
    );
  }, [orderedSegments, search]);

  const rankById = useMemo(
    () => rankByBlock(filteredSegments.filter((s) => s.stagedStatus !== "deleted")),
    [filteredSegments]
  );

  const categoryPositionById = useMemo(() => categoryBlockPositions(filteredSegments), [filteredSegments]);

  const activeSegments = useMemo(() => orderedSegments.filter((s) => s.stagedStatus !== "deleted"), [orderedSegments]);

  const categoryHeaderById = useMemo(() => {
    const headers = new Map<number, CategoryHeaderInfo>();
    filteredSegments.forEach((segment) => {
      const segmentCategory = segment.segment_category;
      if (!segmentCategory || !categoryPositionById.get(segment.id)?.isFirst) return;
      headers.set(segment.id, {
        segmentCategory,
        bucketCount: activeSegments.filter((s) => s.segment_category === segmentCategory).length,
        rank: rankById.get(segment.id),
      });
    });
    return headers;
  }, [filteredSegments, categoryPositionById, activeSegments, rankById]);

  const firstGeneralId = useMemo(() => {
    if (!filteredSegments.some(isExclusive)) return undefined;
    return filteredSegments.find((s) => !isExclusive(s))?.id;
  }, [filteredSegments]);

  const [isCrossTypeDrag, setIsCrossTypeDrag] = useState(false);

  const pageRowIds = useMemo(() => {
    const {page, pageSize} = paginationModel;
    return filteredSegments.slice(page * pageSize, (page + 1) * pageSize).map((s) => s.id);
  }, [filteredSegments, paginationModel]);

  const isSameType = useCallback(
    (firstId: number | string, secondId: number | string) =>
      isExclusive(segmentById.get(Number(firstId))) === isExclusive(segmentById.get(Number(secondId))),
    [segmentById]
  );

  const pageBlockKeys = useMemo(
    () =>
      pageRowIds.map((id) => {
        const segment = segmentById.get(id);
        return segment ? blockKeyOf(segment) : undefined;
      }),
    [pageRowIds, segmentById]
  );

  const blockSortingStrategy = useMemo<SortingStrategy>(
    () => (args) => {
      const activeId = pageRowIds[args.activeIndex];
      const overId = pageRowIds[args.overIndex];
      if (activeId === undefined || overId === undefined || !isSameType(activeId, overId)) return null;
      if (args.index === args.activeIndex) return verticalListSortingStrategy(args);
      const y = blockDisplacement({...args, blockKeys: pageBlockKeys});
      return y ? {x: 0, y, scaleX: 1, scaleY: 1} : null;
    },
    [pageRowIds, pageBlockKeys, isSameType]
  );

  const [categoryDrag, setCategoryDrag] = useState(IDLE_CATEGORY_DRAG);

  const handleDragStart = useCallback(
    ({active}: DragStartEvent) => {
      const segment = segmentById.get(Number(active.id));
      if (!segment?.segment_category) return;
      setCategoryDrag({draggedId: segment.id, draggedCategory: segment.segment_category});
    },
    [segmentById]
  );

  const resetDrag = useCallback(() => {
    setIsCrossTypeDrag(false);
    setCategoryDrag(IDLE_CATEGORY_DRAG);
  }, []);

  const handleDragOver = useCallback(
    ({active, over}: DragOverEvent) => setIsCrossTypeDrag(Boolean(over) && !isSameType(active.id, over!.id)),
    [isSameType]
  );

  const isSearching = search.trim().length > 0;
  const dndEnabled = !isSearching;

  const onDelete = (segment: StagedSegment) => {
    if (segment.stagedStatus === "new") {
      stageDelete(segment.id);
      return;
    }
    NiceModal.show(DeleteSegmentDialog, {
      segmentId: String(segment.id),
      segmentName: segment.name,
      onConfirm: () => stageDelete(segment.id),
    });
  };

  const openSegment = (segment: StagedSegment) => {
    if (segment.stagedStatus === "deleted") return;
    NiceModal.show(SegmentDialog, {
      segment,
      pageKind,
      segments: activeSegments,
      onStage: (payload: ReachSegmentCreatePayload) => stageUpdate(segment.id, payload),
      onOpenSegment: openSegment,
    });
  };

  const addSegment = () =>
    NiceModal.show(SegmentDialog, {
      pageKind,
      segments: activeSegments,
      onStage: stageCreate,
      onOpenSegment: openSegment,
    });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;
      resetDrag();
      if (!over || active.id === over.id || !isSameType(active.id, over.id)) return;
      stageOrder(moveBlock(orderedSegments, Number(active.id), Number(over.id)).map((s) => s.id));
    },
    [orderedSegments, isSameType, stageOrder, resetDrag]
  );

  const handleSaveAll = () => {
    saveSegmentsBatch(buildBatchPayload(), {
      onSuccess: () => {
        toast.success(
          pageKind === "marketing" ? "Segments saved. Audiences are being recalculated." : "Segments saved"
        );
        discardAll();
      },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Failed to save segments"),
    });
  };

  const columns = useMemo<GridColDef<StagedSegment>[]>(
    () => [
      {
        field: "__drag__",
        headerName: "",
        width: 48,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({row}) => (row.segment_category ? null : <DragHandleCell />),
      },
      {
        field: "priority",
        headerName: "#",
        width: 70,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({row}) =>
          row.segment_category ? null : (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                backgroundColor: isExclusive(row) ? Colors.blue._600 : Colors.tertiary._100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Text
                size={12}
                variant="inter600"
                sx={{color: isExclusive(row) ? Colors.bw.white : Colors.grey._700, textAlign: "center"}}
              >
                {rankById.get(row.id) ?? "–"}
              </Text>
            </Box>
          ),
      },
      {
        field: "name",
        headerName: "Segment name",
        flex: 2,
        renderCell: ({row}) => (
          <Box sx={{minWidth: 0, pl: row.segment_category ? 2 : 0}}>
            <Container row sx={{gap: 1, alignItems: "center", justifyContent: "flex-start"}}>
              <Text
                size={14}
                variant="inter600"
                sx={{color: Colors.grey._900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.name}
              </Text>
              {row.stagedStatus && (
                <Chip
                  size_="small"
                  variant_={STAGED_STATUS_CHIP[row.stagedStatus].variant}
                  label={STAGED_STATUS_CHIP[row.stagedStatus].label}
                />
              )}
            </Container>
            {row.description && (
              <Text
                size={13}
                sx={{color: Colors.grey._500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.description}
              </Text>
            )}
          </Box>
        ),
      },
      ...(pageKind === "marketing"
        ? [
            {
              field: "type",
              headerName: "Type",
              width: 110,
              sortable: false,
              renderCell: ({row}) => (
                <Chip
                  size_="small"
                  variant_={isExclusive(row) ? "info" : "inherit"}
                  label={SEGMENT_TYPE_INFO[row.type].label}
                />
              ),
            } satisfies GridColDef<StagedSegment>,
          ]
        : []),
      {
        field: "conditions",
        headerName: "Conditions",
        flex: 2,
        sortable: false,
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
            {row.is_category_fallback && (
              <Chip size_="small" variant_="inherit" label={CATEGORY_FALLBACK_CONDITIONS_LABEL} />
            )}
            {row.conditions.rules.map((rule, i) => (
              <Chip
                key={i}
                size_="small"
                variant_="blue"
                label={"rules" in rule ? rule.name || `Group ${i + 1}` : `Group ${i + 1}`}
              />
            ))}
          </Box>
        ),
      },
      {
        field: "matching",
        headerName: "Matching",
        flex: 1,
        sortable: false,
        renderCell: ({row}) => {
          if (row.is_category_fallback) return null;
          const matchMode = row.conditions.match_mode.charAt(0).toUpperCase() + row.conditions.match_mode.slice(1);
          return <Chip size_="small" variant_="inherit" label={`${matchMode}`} />;
        },
      },
      {
        field: "principal",
        headerName: "Created by",
        flex: 1,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        renderCell: ({row}) => {
          const undoAction = row.stagedStatus
            ? [{label: "Undo changes", icon: <UndoIcon sx={{width: 20, height: 20}} />, onClick: () => undo(row.id)}]
            : [];
          if (row.stagedStatus === "deleted")
            return row.is_category_fallback ? null : <RowActions extraActions={undoAction} />;
          return (
            <RowActions
              onEdit={() => openSegment(row)}
              onDelete={row.is_category_fallback ? undefined : () => onDelete(row)}
              extraActions={undoAction}
            />
          );
        },
      },
    ],
    [rankById, activeSegments, pageKind, undo, stageDelete, stageUpdate, stageCreate]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title={PAGE_TITLE_BY_PAGE_KIND[pageKind]}>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPaginationModel((m) => ({...m, page: 0}));
          }}
          placeholder="Search segments..."
        />
        <Button buttonType="contained-primary" size="small" onClick={addSegment}>
          + Add segment
        </Button>
      </ListPageHeader>
      {hasChanges && (
        <Container
          row
          fullWidth
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            mb: 2,
            borderRadius: "12px",
            backgroundColor: Colors.blue._100,
          }}
        >
          <Box>
            <Text size={13} variant="inter600" sx={{color: Colors.blue._800}}>
              {changeCount} unsaved change{changeCount > 1 ? "s" : ""}
            </Text>
            {pageKind === "marketing" && (
              <Text size={12} sx={{color: Colors.blue._800}}>
                Saving will recalculate audiences once
              </Text>
            )}
          </Box>
          <Container row sx={{gap: 1}}>
            <Button
              buttonType="outlined-primary"
              size="small"
              onClick={discardAll}
              disabled={isSaving}
              sx={{
                borderColor: Colors.blue._300,
                color: Colors.blue._800,
                "&:hover": {borderColor: Colors.blue._500, backgroundColor: Colors.blue._200},
              }}
            >
              Discard all
            </Button>
            <Button buttonType="contained-primary" size="small" onClick={handleSaveAll} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save all"}
            </Button>
          </Container>
        </Container>
      )}
      {error ? (
        <Text size={14}>Failed to load segments.</Text>
      ) : (
        <DndEnabledContext.Provider value={dndEnabled}>
          <SegmentTypeSeparatorContext.Provider value={{firstGeneralId, isBlocked: isCrossTypeDrag}}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={resetDrag}
            >
              <SortableContext items={pageRowIds} strategy={blockSortingStrategy}>
                <CategoryDragContext.Provider value={categoryDrag}>
                  <CategoryHeaderContext.Provider value={categoryHeaderById}>
                    <ListDataGrid
                      rows={filteredSegments}
                      columns={columns}
                      loading={isLoading}
                      getRowId={(row) => row.id}
                      getRowClassName={({row}) =>
                        [
                          row.id === firstGeneralId ? FIRST_GENERAL_ROW_CLASS_NAME : "",
                          row.stagedStatus === "deleted" ? DELETED_ROW_CLASS_NAME : "",
                          row.segment_category
                            ? `${CATEGORY_ROW_CLASS_NAME} ${CATEGORY_ROW_CLASS_NAME}-${row.segment_category}`
                            : "",
                          categoryPositionById.get(row.id)?.isFirst ? CATEGORY_FIRST_ROW_CLASS_NAME : "",
                        ].join(" ")
                      }
                      getRowSpacing={({id}) => {
                        const categoryPosition = categoryPositionById.get(Number(id));
                        const separatorTop = id === firstGeneralId ? SEGMENT_TYPE_SEPARATOR_HEIGHT : 0;
                        const categoryTop = categoryPosition?.isFirst
                          ? CATEGORY_BLOCK_SPACING + CATEGORY_HEADER_HEIGHT
                          : 0;
                        return {
                          top: separatorTop + categoryTop,
                          bottom: categoryPosition?.isLast ? CATEGORY_BLOCK_SPACING : 0,
                        };
                      }}
                      sx={{
                        [`& .MuiDataGrid-row.${FIRST_GENERAL_ROW_CLASS_NAME}`]: {
                          borderBottom: `1px solid ${Colors.tertiary._200}`,
                        },
                        [`& .MuiDataGrid-row.${DELETED_ROW_CLASS_NAME}`]: {
                          opacity: 0.5,
                          textDecoration: "line-through",
                        },
                        ...Object.fromEntries(
                          SEGMENT_CATEGORIES.map((segmentCategory) => [
                            `& .MuiDataGrid-row.${CATEGORY_ROW_CLASS_NAME}-${segmentCategory}`,
                            {
                              backgroundColor: SEGMENT_CATEGORY_INFO[segmentCategory].rowBackground,
                              boxShadow: `inset ${CATEGORY_ACCENT_WIDTH}px 0 0 ${SEGMENT_CATEGORY_INFO[segmentCategory].color}`,
                            },
                          ])
                        ),
                        [`& .MuiDataGrid-row.${CATEGORY_ROW_CLASS_NAME}:not(.${CATEGORY_FIRST_ROW_CLASS_NAME}) .MuiDataGrid-cell`]:
                          {
                            borderTopColor: "transparent",
                          },
                      }}
                      onRowClick={(params) => openSegment(params.row)}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                      slots={{row: SortableSegmentRow}}
                      noRowsText={isSearching ? "No segments match your search" : "No segments yet"}
                    />
                  </CategoryHeaderContext.Provider>
                </CategoryDragContext.Provider>
              </SortableContext>
            </DndContext>
          </SegmentTypeSeparatorContext.Provider>
        </DndEnabledContext.Provider>
      )}
    </Box>
  );
};

export default SegmentsPage;
